import { createSignal } from "solid-js";
import { SuggestedQuestion } from "../types/suggestquestions";
import { CaseState } from "../types/caseState";
import { Utterance } from "../types/utterance";
import { detectQuestions } from "../api/detectQuestions";
import { fetchContextSuggestions } from "../api/contextSuggestions";
import { cosine, embed } from "../utils/embeddings";
import {
  AI_CONFIDENCE_THRESHOLD,
  DETECTOR_COOLDOWN_MS,
  DETECTOR_MAX_PER_MIN,
  SUGGESTIONS_PER_BUCKET,
  RECENT_SUGGESTION_TTL_MS,
} from "../config/interviewConfig";

type PriorityTag = "Critical" | "Important" | "Informational";

type SuggestedLogItem = {
  question: string;
  priority_level?: PriorityTag;
  first_seen: string;
  last_seen: string;
  seen_count: number;
  asked?: boolean;
  dismissed?: boolean;
};

const seenLog = new Map<string, SuggestedLogItem>();
let totalEmitted = 0;

// Cache embeddings by normalized question text so template questions

const embeddingCache = new Map<string, Float32Array>();

const qKey = (q: string) => (q || "").trim().toLowerCase();

const autoMovedOriginals = new Map<string, SuggestedQuestion>();
export function useQuestionSuggestions(
  utterances?: () => Utterance[],
  markQuestionAsked?: (q: string, answer?: string, meta?: any) => void,
) {
  // state
  const [suggestions, setSuggestions] = createSignal<SuggestedQuestion[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  // AI detection state and safety
  const [lastDetectionTime, setLastDetectionTime] = createSignal<number>(0);
  const [detectionCount, setDetectionCount] = createSignal<number>(0);

  const [earlier, setEarlier] = createSignal<SuggestedQuestion[]>([]);

  // Recently shown suggestions TTL (ms) - from config
  // Map of normalized question -> lastShown timestamp
  const recentlyShown = new Map<string, number>();

  function normalize(text: string) {
    return (text || "").trim().toLowerCase();
  }

  let currentCtrl: AbortController | null = null;
  function cancel() {
    currentCtrl?.abort();
    currentCtrl = null;
  }

  // Helper to get last N seconds of transcript
  function getRecentTranscript(seconds: number = 30): string {
    if (!utterances) return "";

    const now = new Date();
    const cutoffTime = new Date(now.getTime() - seconds * 1000);

    return utterances()
      .filter((u) => {
        if (!u.timestamp) return true; // Include if no timestamp
        const uttTime = new Date(u.timestamp);
        return uttTime >= cutoffTime;
      })
      .map((u) => `[Party ${u.speaker}]: ${u.text}`)

      .join("\n");
  }

  async function fetchSuggestions(
    transcriptWindow: string,
    caseState: CaseState,
    contactType: string,
  ) {
    if (!transcriptWindow.trim()) {
      setSuggestions([]);
      return;
    }
    cancel();
    currentCtrl = new AbortController();
    setLoading(true);
    setError(null);
    try {
      console.log("🎯 Fetching suggestions with:", {
        transcriptLength: transcriptWindow.length,
        contactType,
        askedCount: caseState.askedQuestions.length,
      });

      const body = await fetchContextSuggestions({
        transcriptWindow,
        caseState,
        askedQuestions: caseState.askedQuestions,
        dismissedQuestions: caseState.dismissedQuestions,
        contactType,
        includeAllTemplateFields: true,
        signal: currentCtrl?.signal,
      });
      // Merge server suggestions with locally dismissed so dismissals persist visually
      const server = (body.suggested_questions || []) as SuggestedQuestion[];
      const latestServerSuggestions = server;
      const dismissed = (caseState.dismissedQuestions ||
        []) as SuggestedQuestion[];

      const keyof = (q: SuggestedQuestion) => {
        const idPart =
          typeof q.id === "string" ? q.id.trim() : String(q.id ?? "");
        return (idPart || q.question.trim()).toLowerCase();
      };

      // 1) Merge and mark dismissed
      const map = new Map<string, SuggestedQuestion>();
      for (const q of server) {
        map.set(keyof(q), q);
      }
      for (const dq of dismissed) {
        const k = keyof(dq);
        const existing = map.get(k);
        map.set(k, { ...(existing || dq), dismissed: true });
      }

      // 2) Filter out asked and answered entirely
      const askedSet = new Set((caseState.askedQuestions || []).map(normalize));
      const answeredSet = new Set(
        (caseState.answeredQuestions || []).map((qa) => normalize(qa.question)),
      );

      let merged = Array.from(map.values()).filter((q) => {
        const nq = normalize(q.question);
        return !askedSet.has(nq) && !answeredSet.has(nq);
      });

      // 3) (Optional) TTL suppression — disabled when RECENT_SUGGESTION_TTL_MS = 0
      const nowTs = Date.now();
      if (RECENT_SUGGESTION_TTL_MS > 0) {
        merged = merged.filter((q) => {
          const nq = normalize(q.question);
          const last = recentlyShown.get(nq);
          if (!last) return true;
          return nowTs - last > RECENT_SUGGESTION_TTL_MS;
        });
      }

      // 4) Client-side intra-batch de-duplication
      // Uses: semantic similarity (cosine >= 0.9) and text normalization.
      // Both template and context questions for the same field are kept —
      // cosine catches near-identical phrasing, text normalization catches exact dupes.
      const deduped: SuggestedQuestion[] = [];
      const vectors: Float32Array[] = [];
      const seenText = new Map<string, SuggestedQuestion>(); // normalized text -> question

      for (const q of merged) {
        const textKey = normalize(q.question);

        // Check 1: Exact text dedup (normalized)
        if (seenText.has(textKey)) continue;

        // Check 2: Semantic similarity via embeddings (cached)
        try {
          let v = embeddingCache.get(textKey);
          if (!v) {
            v = await embed(q.question, currentCtrl?.signal);
            embeddingCache.set(textKey, v);
          }
          let isDuplicate = false;
          for (let i = 0; i < vectors.length; i++) {
            const sim = cosine(v, vectors[i]);
            if (sim >= 0.9) {
              isDuplicate = true;
              break;
            }
          }

          if (!isDuplicate) {
            seenText.set(textKey, q);
            deduped.push(q);
            vectors.push(v);
          }
        } catch {
          // On embedding error, fall back to text-based check only
          if (!seenText.has(textKey)) {
            seenText.set(textKey, q);
            deduped.push(q);
          }
        }
      }

      // 5) Cap per bucket by source (template/context) so both surface
      const priorities = ["Critical", "Important", "Informational"];
      let finalList: SuggestedQuestion[] = [];
      for (const p of priorities) {
        const bySource = {
          template: [] as SuggestedQuestion[],
          context: [] as SuggestedQuestion[],
        };
        for (const q of deduped) {
          const lvl = String(q.priority_level || "Informational");
          if (lvl !== p) continue;
          const src = q.source === "context" ? "context" : "template";
          bySource[src].push(q);
        }
        finalList = finalList.concat(
          bySource.template.slice(0, SUGGESTIONS_PER_BUCKET),
          bySource.context.slice(0, SUGGESTIONS_PER_BUCKET),
        );
      }

      // Snooze semantics: if a question is in Earlier, do not re-promote it
      try {
        const earlierSet = new Set(
          (earlier() || []).map((e) => normalize(e.question)),
        );
        finalList = finalList.filter(
          (q) => !earlierSet.has(normalize(q.question)),
        );
      } catch {}

      // 6) Update recentlyShown timestamps for emitted items
      for (const q of finalList) {
        recentlyShown.set(normalize(q.question), nowTs);
      }
      // ADD: keep a dismissed lookup so earlier items reflect current dismissed state
      const dismissedSet = new Set(
        ((caseState.dismissedQuestions || []) as any[]).map((d) =>
          typeof d === "string" ? normalize(d) : normalize(d.question),
        ),
      );

      // What was shown previously in the MAIN list and is now dropping out?
      const prev = suggestions();
      const finalKeys = new Set(finalList.map((q) => normalize(q.question)));

      const candidatesToEarlier = prev.filter((q) => {
        const nq = normalize(q.question);
        return (
          !finalKeys.has(nq) && // no longer in main
          !askedSet.has(nq) && // not asked
          !answeredSet.has(nq) && // not answered
          !dismissedSet.has(nq)
        );
      });

      // Merge into "earlier" (dedupe by text), and mark dismissed using case state
      const byKey = new Map<string, SuggestedQuestion>();
      for (const q of [...earlier(), ...candidatesToEarlier]) {
        const k = normalize(q.question);
        if (dismissedSet.has(k)) continue;
        byKey.set(k, {
          ...q,
          dismissed: dismissedSet.has(k) || !!q.dismissed,
        });
      }

      // Snooze semantics: keep items in earlier until manually restored
      for (const nq of askedSet) byKey.delete(nq);
      for (const nq of answeredSet) byKey.delete(nq);
      // Commit earlier list
      setEarlier(Array.from(byKey.values()));

      setSuggestions(finalList);

      const nowIso = new Date().toISOString();
      for (const item of finalList) {
        const key = qKey(item.question);
        const existing = seenLog.get(key);
        if (existing) {
          existing.last_seen = nowIso;
          existing.seen_count += 1;
          existing.priority_level =
            item.priority_level || existing.priority_level;
        } else {
          seenLog.set(key, {
            question: item.question,
            priority_level: item.priority_level,
            first_seen: nowIso,
            last_seen: nowIso,
            seen_count: 1,
          });
        }
      }
      totalEmitted += finalList.length;

      console.log(
        "✅ Suggestions fetched successfully:",
        body.suggested_questions?.length || 0,
      );

      // AI-based automatic detection (disabled for manual DnD-only mode)
      if (
        utterances &&
        markQuestionAsked &&
        body.suggested_questions?.length > 0
      ) {
        const recentTranscript = getRecentTranscript(30);

        if (recentTranscript.length > 100) {
          // Minimum content threshold
          // Less aggressive rate limiting → now throttled to ~10s
          const now = Date.now();
          const timeSinceLastDetection = now - lastDetectionTime();
          const cooldownPeriod = DETECTOR_COOLDOWN_MS;
          const maxDetectionsPerMinute = DETECTOR_MAX_PER_MIN;

          // Reset detection count every minute
          if (timeSinceLastDetection > 60000) {
            setDetectionCount(0);
          }

          const canDetect =
            timeSinceLastDetection > cooldownPeriod &&
            detectionCount() < maxDetectionsPerMinute;

          if (canDetect) {
            setLastDetectionTime(now);
            setDetectionCount((prev) => prev + 1);

            console.log("Running AI question detection...", {
              recentTranscriptLength: recentTranscript.length,
              suggestedCount: body.suggested_questions.length,
              alreadyAsked: caseState.askedQuestions,
              alreadyAnswered: caseState.answeredQuestions.map(
                (qa) => qa.question,
              ),
              transcriptSample: recentTranscript.slice(0, 200) + "...",
            });

            // Call AI detection endpoint with current state
            detectQuestions({
              recentTranscript,
              suggestedQuestions: body.suggested_questions,
              alreadyAsked: [
                ...caseState.askedQuestions,
                ...caseState.answeredQuestions.map((qa) => qa.question),
              ],
              sessionId: (window as any).__sessionId || null,
              contactType,
              signal: currentCtrl?.signal,
            })
              .then((detectionResult) => {
                const detected = detectionResult.detectedQuestions || [];

                console.log("AI detection results:", detected);

                // Process each detected question
                detected.forEach((d: any) => {
                  const qText = (d.question || "").trim();
                  const conf = Number(d.confidence || 0);

                  if (!qText) return;
                  if (conf < AI_CONFIDENCE_THRESHOLD) return;

                  // Remember original suggestion so we can “Return” later
                  const norm = (t: string) => (t || "").trim().toLowerCase();
                  const orig = latestServerSuggestions.find(
                    (s) => norm(s.question) === norm(qText),
                  );
                  // Resolve priority by checking server batch → live suggestions → earlier; fallback to Informational
                  let resolvedPriority: PriorityTag | undefined =
                    orig?.priority_level as PriorityTag | undefined;
                  if (!resolvedPriority) {
                    try {
                      const fromLive = (suggestions() || []).find(
                        (s) => norm(s.question) === norm(qText),
                      );
                      resolvedPriority = fromLive?.priority_level as
                        | PriorityTag
                        | undefined;
                    } catch {}
                  }
                  if (!resolvedPriority) {
                    try {
                      const fromEarlier = (earlier() || []).find(
                        (s) => norm(s.question) === norm(qText),
                      );
                      resolvedPriority = fromEarlier?.priority_level as
                        | PriorityTag
                        | undefined;
                    } catch {}
                  }
                  const priority_level: PriorityTag =
                    (resolvedPriority as PriorityTag) || "Informational";
                  if (orig) autoMovedOriginals.set(qText, orig);

                  // Immediately remove from local suggestions for better UX
                  setSuggestions((prev) =>
                    prev.filter((q) => q.question !== qText),
                  );
                  console.log("✅ Auto-marking question (AI):", {
                    question: qText,
                    confidence: conf,
                  });

                  markQuestionAsked(
                    qText,
                    d.answer && d.answer !== "No clear answer"
                      ? d.answer
                      : undefined,
                    {
                      source: "ai",
                      origin: orig?.source as
                        | "template"
                        | "context"
                        | undefined,
                      confidence: conf,
                      priority_level,
                      fieldKey: orig?.fieldKey,
                    },
                  );
                });
              })
              .catch((err) => {
                if (err.name !== "AbortError") {
                  console.error("AI detection error:", err);
                }
              });
          } else {
            console.log("🚫 AI detection skipped (cooldown/rate limit)");
          }
        }
      }
    } catch (e: any) {
      if (e.name === "AbortError") return;
      console.error("Error fetching suggestions:", e);
      setError(e.message || "Unknown error");
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }

  function addBackToSuggestions(q: SuggestedQuestion) {
    setSuggestions((prev) => {
      const keyOf = (s: SuggestedQuestion) =>
        (s.id || s.question).trim().toLowerCase();
      const seen = new Set(prev.map(keyOf));
      if (seen.has(keyOf(q))) return prev;
      return [q, ...prev];
    });
    setEarlier((prev) =>
      prev.filter((it) => normalize(it.question) !== normalize(q.question)),
    );
  }

  function removeFromSuggestions(questionText: string) {
    setSuggestions((prev) => prev.filter((q) => q.question !== questionText));
  }

  function clearSuggestions() {
    // Guard: Do not clear suggestions if we are in file mode.
    // This prevents the race condition where the session returns to Idle
    // and wipes the UI before the Auto-Stop logic can save the questions.
    const isFileMode = (window as any)._captureMode === "file";
    if (isFileMode) {
      console.log("[useQuestionSuggestions] clearSuggestions blocked (file mode active)");
      return;
    }
    setSuggestions([]);
    setError(null);
    setLoading(false);
  }

  // helpers for persistence restore
  function setEarlierFromPersisted(
    list: SuggestedQuestion[] | undefined | null,
  ) {
    try {
      const arr = Array.isArray(list) ? list : [];
      console.log("setEarlierFromPersisted", arr);
      setEarlier(arr);
    } catch {
      setEarlier([]);
    }
  }

  function replaceSuggestions(list: SuggestedQuestion[] | undefined | null) {
    try {
      const arr = Array.isArray(list) ? list : [];
      setSuggestions(arr);
    } catch {
      setSuggestions([]);
    }
  }

  return {
    suggestions,
    loading,
    error,
    earlier,
    fetchSuggestions,
    cancelSuggestions: cancel,
    addBackToSuggestions,
    removeFromSuggestions,
    getAutoMovedOriginal: (q: string) => autoMovedOriginals.get(q),
    clearAutoMovedOriginal: (q: string) => autoMovedOriginals.delete(q),
    clearSuggestions,
    setEarlierFromPersisted,
    replaceSuggestions,
  };
}
export function getSuggestedQuestionsSnapshot(): SuggestedQuestion[] {
  return Array.from(seenLog.values())
    .map((s) => ({
      id: s.question,
      question: (s.question || "").trim(),
      source: (s as any).source || undefined,
      priority_level: (s.priority_level ?? "Informational") as PriorityTag,
      importance: "",
    }))
    .filter((s) => !!s.question);
}

export function resetSuggestionsHistory() {
  seenLog.clear();
  totalEmitted = 0;
  autoMovedOriginals.clear();
  embeddingCache.clear();
}
